/**
 * Role -> section visibility. The single source of truth for role gating.
 *
 * ⚠️  DEMO-LEVEL ONLY. This hides UI. It is NOT security.
 *
 * The API has no per-role read authorization: every data route is gated only on
 * "is there a valid token?" (middleware/auth.py:get_current_user), plus a write
 * block on role === 'viewer' (require_write) and pastor-only DELETEs. So any
 * authenticated non-viewer account can still read every endpoint directly with
 * curl, whatever this file hides in the nav.
 *
 * Keep this table as the one place roles are declared. When the real
 * read-authorization layer is built server-side, it should be a port of THIS
 * table, so the UI and the API cannot drift apart.
 */

/**
 * True while role gating is UI-only. Drives the banner in Layout.jsx.
 * Flip to false ONLY when the server enforces the same table, so the banner
 * disappears in the same change that makes it untrue.
 */
export const ROLE_GATING_IS_UI_ONLY = true;

// Every section the app knows about. Key -> nav label.
export const SECTIONS = {
  members: 'Members',
  households: 'Households',
  finance: 'Finance',
  financials: 'Financials', // the API enforces this one server-side too (routes/financials.py READ_ROLES)
  groups: 'Groups',
  events: 'Events',
  comms: 'Comms',
  checkin: 'Check-in',
  content: 'Sermons',
  cameras: 'Cameras', // declared for deliverable B12; no route built yet
};

const ALL = Object.keys(SECTIONS);

/**
 * Who sees what.
 *
 * - admin / pastor: everything. Sermons are the pastor's, per the church's intent.
 * - viewer: the PUBLIC read-only demo account (scripts/seed_demo.py). Deliberately
 *   broad so the "Try a demo" button still shows a full app. require_write already
 *   blocks it from changing anything.
 * - deacon: cameras are the deacon's, per the camera footage policy.
 * - member: minimal. The full member-facing set is still an open decision.
 */
export const ROLE_SECTIONS = {
  admin: ALL,
  pastor: ALL,
  viewer: ALL.filter((s) => s !== 'cameras'),
  staff: ['members', 'households', 'groups', 'events', 'comms', 'checkin'],
  deacon: ['members', 'events', 'cameras'],
  member: ['events'],
};

// An unrecognised role gets the most restrictive set, never a default-open one.
export const DEFAULT_SECTIONS = ['events'];

export function sectionsFor(role) {
  return ROLE_SECTIONS[role] || DEFAULT_SECTIONS;
}

export function canSee(role, section) {
  return sectionsFor(role).includes(section);
}
